import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GoogleMapView } from '@/features/map/components/GoogleMapView';
import { ZoomButtons } from '@/features/map/components/ZoomButtons';
import { Logo } from '../common/Logo';
import { LocationPermissionPrompt } from '@/features/search/components';
import { QuickFilterChips } from '@/features/filters/components/QuickFilterChips';
import { MapSearchBar } from '@/features/search/components';
import { FilterPanel } from '@/features/filters/components/FilterPanel';
import { ChargePointInfoPanel } from '@/features/charge-points/components/info-panel/ChargePointInfoPanel';
import { LanguageSwitcher } from '@/features/locale/components/LanguageSwitcher';
import { ThemeToggle } from '@/features/theme/components/ThemeToggle';
import { ShareButton } from '@/features/share/components/ShareButton';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { NearbyCarousel } from '@/features/charge-points/components/nearby/NearbyCarousel';
import { useFilterStore } from '@/features/filters/store/filter-store';
import {
  useChargePointsStore,
  type ChargePointsState,
} from '@/features/charge-points/store/charge-points-store';
import { useGoogleMapsStore } from '@/features/map/store/google-maps-store';
import { useTranslation } from 'react-i18next';

/**
 * Mobile layout (<800px)
 */
export function MobileLayout() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const isFilterPanelOpen = useFilterStore((s) => s.isFilterPanelOpen);
  const toggleFilterPanel = useFilterStore((s) => s.toggleFilterPanel);
  const controlsEnabled = useFilterStore((s) => s.controlsEnabled);
  const showShareButton = useFilterStore((s) => s.showShareButton);
  const isDetailsPanelOpen = useChargePointsStore(
    (s: ChargePointsState) => s.isDetailsPanelOpen
  );
  const closeDetails = useChargePointsStore(
    (s: ChargePointsState) => s.closeDetails
  );
  const setShouldInterceptPointer = useGoogleMapsStore(
    (s) => s.setShouldInterceptPointer
  );

  // Check if map is locked
  const isLocked = searchParams.get('locked') === 'true';

  // The filter bottom sheet (statuses, charge speed, price, operators, best
  // price, etc.) is entirely charge-point-specific and doesn't apply to the
  // sessions map, so hide it in charge-sessions mode. Read `mapType` the same
  // way GoogleMapView does. STOPGAP — per-map-type filter/config mapping is
  // tracked in BIZOPS-1600.
  const isChargeSessionsMode = searchParams.get('mapType') === 'chargeSessions';
  const showFilterUi =
    !isChargeSessionsMode && (controlsEnabled || isFilterPanelOpen);

  // Selecting a charger shows its card; tapping the card opens the full
  // details sheet. Embeds (no controls) keep opening the sheet directly.
  const useCards = controlsEnabled && !isChargeSessionsMode;
  const selectedId = useChargePointsStore((s) => s.selectedChargePoint?.id);
  const [isSheetExpanded, setSheetExpanded] = useState(false);
  useEffect(() => setSheetExpanded(false), [selectedId]);
  const isDetailsSheetOpen =
    isDetailsPanelOpen && (!useCards || isSheetExpanded);
  const showCards = useCards && !isDetailsSheetOpen && !isFilterPanelOpen;

  // Disable map interactions while a bottom sheet is open
  useEffect(() => {
    setShouldInterceptPointer(isDetailsSheetOpen || isFilterPanelOpen);
  }, [isDetailsSheetOpen, isFilterPanelOpen, setShouldInterceptPointer]);

  // Floating buttons sit above the charger cards
  const floatingBottom = showCards
    ? 'calc(env(safe-area-inset-bottom) + 17rem)'
    : 'max(calc(env(safe-area-inset-bottom) + 5rem), 6rem)';

  return (
    <div className="relative h-screen w-screen overflow-hidden">
      {/* Full screen map */}
      <GoogleMapView />

      {/* Top: logo + language row, then search and scrollable quick filter
          chips (charge-point-specific, hidden in charge-sessions mode) */}
      <div
        className="absolute inset-x-4 z-10 flex flex-col gap-2 pointer-events-none"
        style={{ top: 'max(env(safe-area-inset-top), 1rem)' }}
      >
        <div className="flex items-center justify-between">
          <div className="pointer-events-auto">
            {controlsEnabled ? (
              <Logo />
            ) : (
              <a
                href="https://monta.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Logo />
              </a>
            )}
          </div>
          {controlsEnabled && (
            <div className="pointer-events-auto flex items-center gap-2">
              <ThemeToggle />
              <LanguageSwitcher />
            </div>
          )}
        </div>
        {showFilterUi && (
          <div className="pointer-events-auto flex flex-col gap-2">
            <MapSearchBar />
            {controlsEnabled && <QuickFilterChips />}
          </div>
        )}
      </div>

      {/* Share button - bottom-left, above the cards / hint */}
      {controlsEnabled && (
        <div
          className="absolute left-4 z-20 pointer-events-none"
          style={{ bottom: floatingBottom }}
        >
          <div className="pointer-events-auto flex items-center gap-2">
            {showShareButton && <ShareButton />}
          </div>
        </div>
      )}

      {/* Location permission prompt */}
      <LocationPermissionPrompt />

      {/* Map controls - bottom right corner (hidden when locked, and while
          the charger cards are shown - pinch-zoom covers it on mobile) */}
      {controlsEnabled && !isLocked && !showCards && (
        <div
          className="absolute right-4 z-10 pointer-events-none"
          style={{ bottom: floatingBottom }}
        >
          <div className="pointer-events-auto flex flex-col gap-3">
            <ZoomButtons />
          </div>
        </div>
      )}

      {/* Bottom: swipeable charger cards, or the hint where cards don't apply */}
      {showCards ? (
        <div
          className="absolute inset-x-0 z-10"
          style={{ bottom: 'max(env(safe-area-inset-bottom), 0.75rem)' }}
        >
          <NearbyCarousel onOpenDetails={() => setSheetExpanded(true)} />
        </div>
      ) : (
        !useCards && (
          <div
            className="absolute left-4 right-4 z-10 pointer-events-none"
            style={{ bottom: 'max(env(safe-area-inset-bottom), 1rem)' }}
          >
            <div className="surface-float text-xs text-muted px-3 py-1 rounded-full text-center">
              {t('map.tapMarkersHint')}
            </div>
          </div>
        )
      )}

      {/* Filter bottom sheet - charge-point-specific, so hidden in
          charge-sessions mode. */}
      {showFilterUi && (
        <BottomSheet
          isOpen={isFilterPanelOpen}
          onClose={toggleFilterPanel}
          title={t('filters.title')}
          initialSnap="full"
        >
          <FilterPanel inBottomSheet />
        </BottomSheet>
      )}

      {/* Charge point details bottom sheet */}
      {/* Charge point details bottom sheet - closing returns to the card */}
      <BottomSheet
        isOpen={isDetailsSheetOpen}
        onClose={useCards ? () => setSheetExpanded(false) : closeDetails}
        fullBleed
      >
        <ChargePointInfoPanel onClose={closeDetails} />
      </BottomSheet>
    </div>
  );
}
